import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Formsy from 'formsy-react';
import { Plus } from 'lucide-react';
import { SelectMulti, Select as TWSelect, Row, Col, Card, Button, Label, FormGroup, Badge, DefaultTooltip, SheetBody, SheetFooter, FormSection } from 'twyne-new.ui';

import FileUpload from '@/components/FileUpload';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import Switch, { PureSwitch } from 'components/form/Switch';
import appStore from 'stores/app';
import history from 'utils/history';

import { useRootfileStore } from '../store';

const MIME_TYPES = ['audio/aac', 'application/javascript', 'application/x-abiword', 'application/x-freearc', 'image/avif', 'video/x-msvideo', 'application/vnd.amazon.ebook', 'application/octet-stream', 'image/bmp', 'application/x-bzip', 'application/x-bzip2', 'application/x-cdf', 'application/x-csh', 'text/css', 'text/csv', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-fontobject', 'aapplication/epub+zip', 'application/gzip', 'image/gif', 'text/html', 'image/vnd.microsoft.icon', 'text/calendar', 'application/java-archive', 'image/jpeg', 'text/javascript', 'application/json', 'application/ld+json', 'audio/midi', 'audio/x-midi', 'audio/mpeg', 'video/mp4', 'video/mpeg', 'application/vnd.apple.installer+xml', 'application/vnd.oasis.opendocument.presentation', 'application/vnd.oasis.opendocument.spreadsheet', 'application/vnd.oasis.opendocument.text', 'audio/ogg', 'video/ogg', 'application/ogg', 'audio/opus', 'font/otf', 'image/png', 'application/pdf', 'application/x-httpd-php', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.rar', 'application/rtf', 'application/x-sh', 'image/svg+xml', 'application/x-shockwave-flash', 'application/x-tar', 'image/tiff', 'video/mp2t', 'font/ttf', 'text/plain', 'application/vnd.visio', 'audio/wav', 'audio/webm', 'video/webm', 'image/webp', 'font/woff', 'font/woff2', 'application/xhtml+xml', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/xml', 'application/atom+xml', 'text/xml', 'application/vnd.mozilla.xul+xml', 'application/zip', 'video/3gpp', 'audio/3gpp', 'video/3gpp2', 'audio/3gpp2', 'application/x-7z-compressed'];

const MIME_TYPE_OPTIONS = MIME_TYPES.map(mimetype => ({ label: mimetype, value: mimetype }));

export default class RootFileForm extends React.Component {
  state = {
    active: true,
    site_id: appStore.site.id,
    isLoadFile: false,
  }

  componentWillMount() {
    const { data } = this.props;
    if (data) {
      if (!MIME_TYPES.includes(data.content_type)) {
        MIME_TYPES.push(data.content_type);
        MIME_TYPE_OPTIONS.push({ label: data.content_type, value: data.content_type })
      }
      this.setState(data);
    }
  }

  handleValidSubmit = model => {
    this.props.handleSubmit(Object.assign(this.state, model));
  }

  handleCancel = () => {
    if (this.props.onCancel) {
      this.props.onCancel();
    } else {
      history.goBack();
    }
  }

  handleFileRemove = () => this.setState({ file: null, content_type: null })

  handleChangeMimeType = option => {
    const contentType = option ? option.value : null;
    this.setState({ content_type: contentType });
  }

  FileDomainUploader = ({ options, data, onRemove, children }) => {
    const [domains, setDomains] = useState(data.domains || null)
    const [file, setFile] = useState(data.file || null)
    const [contentType, setContentType] = useState(data.content_type || null)

    const [loading, setLoading] = useState(false)
    const { upload } = useRootfileStore()

    useEffect(() => {
      data.domains = domains
    }, [domains, data])

    useEffect(() => {
      data.file = file
    }, [file, data])

    useEffect(() => {
      data.content_type = contentType
    }, [contentType, data])

    const handleFileUpload = async file => {
      setLoading(true)
      try {
        const { contentType, location } = await upload(file)

        if (!MIME_TYPES.includes(contentType)) {
          MIME_TYPES.push(contentType)
          MIME_TYPE_OPTIONS.push({ label: contentType, value: contentType })
        }

        setFile(location)
        setContentType(contentType)
      } catch (error) {
        //
      }
      setLoading(false)
    }

    return <Row>
      <Col size='12'>
        <FormGroup>
          <Label>Domain</Label>
          {children}
          <SelectMulti
            value={domains}
            options={options}
            clearable={false}
            onChange={items => setDomains(items.map(item => item.value))}
          />
        </FormGroup>
      </Col>
      <Col size="12">
        <FormGroup>
          <Label>Content Type</Label>
          <TWSelect
            options={MIME_TYPE_OPTIONS}
            value={contentType}
            onChange={option => setContentType(option.value)}
          />
        </FormGroup>
      </Col>
      <Col size="12">
        <FormGroup className='mb-0'>
          <Label>File</Label>
          <FileUpload
            onUpload={handleFileUpload}
            value={file}
            isLoading={loading}
            onRemove={onRemove}
          />
        </FormGroup>
      </Col>
    </Row>
  }

  FilesDomainsUploaders = () => {
    const [files, setFiles] = useState(this.state.meta?.files || [])

    const domainsOtpions = useMemo(() => appStore.site.domain_aliases
      .split(',')
      .filter(domain => !domain.includes('*'))
      .map(domain => {
        domain = JSON.parse(domain)
        return {
          label: domain,
          value: domain,
        }
      }), [])

    useEffect(() => {
      this.setState({
        meta: {
          ...this.state.meta || {},
          files,
        },
      })
    }, [files])

    return <Col size='12'>
      <Row>
        <Col size="12">
          <div className='float-right'>
            <Button
              color="accent"
              size="sm"
              onClick={() => setFiles(files => [...files, {}])}
            >
              <Plus size={14} />
              Add File
            </Button>
          </div>

          <FormSection>
            Files by domain
          </FormSection>
        </Col>
        {files.map((file, idx) => <>
          <Col size="12" key={JSON.stringify(file)}>
            <Card className='pb-2'>
              <FormGroup>
                <this.FileDomainUploader
                  data={file}
                  options={domainsOtpions}
                  onRemove={() => setFiles(files.filter(item => item !== file))}
                >
                  {idx !== 0 ? null : <div className='float-right'>
                    <DefaultTooltip side='left' content='Default file is used when no domain is matched'>
                      <Badge className='mb-2' size='sm' color="accent">default</Badge>
                    </DefaultTooltip>
                  </div>}
                </this.FileDomainUploader>
              </FormGroup>
            </Card>
          </Col>
        </>)}
      </Row>
    </Col>
  }

  Uploader = () => {
    const { meta } = this.state;
    const [multipleDomains, setMultipleDomains] = useState(!!meta?.files)

    const handleChangeMultipleDomains = ({ target: { checked } }) => {
      setMultipleDomains(checked)
      if (!checked) {
        this.setState({ meta: null })
      }
    }

    return <>
      <Col size="3">
        <FormGroup>
          <Label>Multiple domains</Label>
          <PureSwitch
            value={multipleDomains}
            onChange={handleChangeMultipleDomains}
          />
        </FormGroup>
      </Col>
      {multipleDomains ? <this.FilesDomainsUploaders /> : <>
        <Col size="12">
          <FormGroup>
            <Label>Content Type</Label>
            <Select
              name="content_type"
              options={MIME_TYPE_OPTIONS}
              value={this.state.content_type || null}
              handleOnChange={this.handleChangeMimeType}
            />
          </FormGroup>
        </Col>
        <Col size="12">
          <FormGroup>
            <Label>File</Label>
            <this.FileUploader />
          </FormGroup>
        </Col>
      </>}
    </>
  }

  FileUploader = () => {
    const { upload } = useRootfileStore()

    const handleFileUpload = useCallback(async file => {
      try {
        this.setState({ isLoadFile: true })
        const data = await upload(file)

        if (!MIME_TYPES.includes(data.contentType)) {
          MIME_TYPES.push(data.contentType)
          MIME_TYPE_OPTIONS.push({ label: data.contentType, value: data.contentType })
        }

        this.setState({ file: data.location, content_type: data.contentType, isLoadFile: false })
      } catch (error) {
        console.log(error)
        this.handleFileRemove()
      }
    }, [])

    return <FileUpload
      type={null}
      onUpload={handleFileUpload}
      value={this.state.file}
      isLoading={this.state.isLoadFile}
      onRemove={this.handleFileRemove}
    />
  }

  render() {
    return (
      <Formsy onValidSubmit={this.handleValidSubmit}>
        <SheetBody>
          <Row>
            <Col size="7">
              <FormGroup>
                <Label>Path</Label>
                <Input name="path" value={this.state.path || ''} required />
              </FormGroup>
            </Col>
            <Col size="2">
              <FormGroup>
                <Label>Active</Label>
                <Switch name="active" value={this.state.active || false} />
              </FormGroup>
            </Col>
            <this.Uploader />
          </Row>
        </SheetBody>
        <SheetFooter>
          <Button onClick={this.handleCancel} variant='ghost'>Cancel</Button>
          <Button type="submit" color="accent">Save</Button>
        </SheetFooter>
      </Formsy>
    );
  }
}

RootFileForm.propTypes = {
  handleSubmit: PropTypes.func,
  data: PropTypes.any,
};
